import React, { useState, useEffect, useMemo, useRef } from 'react';
import { reportHandledError } from '../utils/sentry';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import AccountReconciliationModal from '../components/AccountReconciliationModal';
import { formatCurrencyAr, parseLocalizedNumber } from '../utils/numberFormat';
import { fetchActiveOrganizationProvidersWithBalances } from '../utils/providerBalances';
import {
  buildProviderPaymentItems,
  buildSuggestedAllocations,
  clampMoney,
  fetchOpenProviderPurchases,
  normalizeMoney,
  parseProviderPaymentApplications,
  recomputeProviderPurchaseStatuses,
  sumAllocationAmounts,
} from '../utils/accountReconciliation';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import { 
  Check, Search, ArrowUpCircle, RefreshCcw, 
  FileText, Calendar, Trash2, AlertCircle,
  Eye, EyeOff, X, DollarSign, Activity, CreditCard, Truck
} from 'lucide-react';
import toast from 'react-hot-toast';

const padDatePart = (value, length = 2) => String(value).padStart(length, '0');

const getLocalDateKey = (date = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${padDatePart(date.getFullYear(), 4)}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

const formatLocalTimestampWithOffset = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return new Date().toISOString();

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = padDatePart(Math.floor(absOffset / 60));
  const offsetMins = padDatePart(absOffset % 60);
  const datePart = getLocalDateKey(date);
  const timePart = `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(date.getSeconds())}.${padDatePart(date.getMilliseconds(), 3)}`;

  return `${datePart}T${timePart}${sign}${offsetHours}:${offsetMins}`;
};

const buildLocalOperationTimestamp = (dateYMD) => {
  if (!dateYMD) return formatLocalTimestampWithOffset(new Date());
  const now = new Date();
  const [year, month, day] = String(dateYMD).split('-').map((segment) => Number(segment));
  if (!year || !month || !day) return formatLocalTimestampWithOffset(now);

  return formatLocalTimestampWithOffset(
    new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()),
  );
};

const buildLocalDateRangeTimestamp = (dateYMD, endOfDay = false) => {
  if (!dateYMD) return null;
  const [year, month, day] = String(dateYMD).split('-').map((segment) => Number(segment));
  if (!year || !month || !day) return null;

  const timeParts = endOfDay ? [23, 59, 59, 999] : [0, 0, 0, 0];
  return formatLocalTimestampWithOffset(new Date(year, month - 1, day, ...timeParts));
};

/**
 * TesoreriaPagos.jsx - Estandarizado al modelo Productos
 * Gestión de Pagos a Proveedores.
 * NUEVO: Interfaz limpia (Header -> KPIs -> Filtros -> Tabla).
 * NUEVO: Anulación inline ("Sí, borrar" / "No") con rollback de Caja, Proveedor y Compras.
 * NUEVO: Integración completa de cuenta corriente (proveedor_movimientos).
 */
function TesoreriaPagos() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Estado para controlar la anulación (Expandir fila y confirmación inline)
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); 
  const [processingDeleteId, setProcessingDeleteId] = useState(null); 
  
  // Filtro de eliminados
  const [mostrarEliminados, setMostrarEliminados] = useState(false);

  const [proveedores, setProveedores] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [busquedaProveedor, setBusquedaProveedor] = useState('');
  const [showProveedorList, setShowProveedorList] = useState(false);
  const proveedorContainerRef = useRef(null);
  const [conciliationOpen, setConciliationOpen] = useState(false);
  const [preparingConciliation, setPreparingConciliation] = useState(false);
  const [conciliationDocs, setConciliationDocs] = useState([]);
  const [conciliationPurchases, setConciliationPurchases] = useState([]);
  const [conciliationContext, setConciliationContext] = useState(null);

  // Filtros de Auditoría (Hoy a Hoy por defecto)
  const today = getLocalDateKey();
  const [fechaDesde, setFechaDesde] = useState(today);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [filtroSitio, setFiltroSitio] = useState('');
  const [q, setQ] = useState(''); // Búsqueda rápida

  const [form, setForm] = useState({ 
    proveedor_id: '', 
    bank_id: '', 
    sitio_id: '', 
    amount: '', 
    obs: '', 
    fecha: today 
  });

  const proveedorSeleccionado = useMemo(
    () => proveedores.find((p) => p.id === form.proveedor_id) || null,
    [proveedores, form.proveedor_id]
  );

  const proveedoresFiltrados = useMemo(() => {
    const needle = busquedaProveedor.trim().toLowerCase();
    if (!needle) return proveedores.slice(0, 25);

    return proveedores
      .filter((p) => {
        const razonSocial = (p.razon_social || '').toLowerCase();
        const cuit = String(p.cuit || '').toLowerCase();
        return razonSocial.includes(needle) || cuit.includes(needle);
      })
      .slice(0, 25);
  }, [proveedores, busquedaProveedor]);

  useEffect(() => {
    if (profile?.organization_id) {
      loadMasters();
      loadHistorial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, fechaDesde, fechaHasta, mostrarEliminados]); 

  // Selección automática si viene de la ficha del proveedor
  useEffect(() => {
    if (location.state?.proveedorId && proveedores.length > 0) {
      setForm(prev => ({ ...prev, proveedor_id: location.state.proveedorId }));
      setBusquedaProveedor('');
    }
  }, [location.state, proveedores]);

  useEffect(() => {
    const siteId = form.sitio_id || filtroSitio;
    if (siteId) persistSelectedSiteId(siteId);
  }, [form.sitio_id, filtroSitio]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (proveedorContainerRef.current && !proveedorContainerRef.current.contains(event.target)) {
        setShowProveedorList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refresco completo: tabla de pagos + saldos de bancos y proveedores.
  const refrescarTodo = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([loadMasters(), loadHistorial()]);
    } finally {
      setRefreshing(false);
    }
  };

  const resetPagoForm = () => {
    const preferredSiteId = resolvePreferredSiteId(sitios, {
      currentSiteId: form.sitio_id || filtroSitio,
    });
    setForm({ proveedor_id: '', bank_id: '', sitio_id: preferredSiteId, amount: '', obs: '', fecha: today });
    setBusquedaProveedor('');
    setShowProveedorList(false);
  };

  const closeConciliationModal = (force = false) => {
    if (saving && !force) return;
    setConciliationOpen(false);
    setConciliationDocs([]);
    setConciliationPurchases([]);
    setConciliationContext(null);
    setPreparingConciliation(false);
  };

  const formatConciliationDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toLocaleDateString('es-AR', { timeZone: 'UTC' });
  };

  const handleConciliationAllocationChange = (documentId, rawValue) => {
    setConciliationDocs((prev) =>
      prev.map((document) =>
        document.id === documentId
          ? {
              ...document,
              allocation: clampMoney(rawValue, document.open_amount),
            }
          : document,
      ),
    );
  };

  async function loadMasters() {
    const orgId = profile.organization_id;
    try {
      const [p, b, s] = await Promise.all([
          fetchActiveOrganizationProvidersWithBalances({ organizationId: orgId, supabaseClient: supabase }),
          supabase.from('banks').select('*').eq('organization_id', orgId).eq('is_active', true).order('name'),
          supabase.from('sitios').select('*').eq('organization_id', orgId).eq('is_active', true).order('nombre')
      ]);
      if (p.error) throw p.error;
      setProveedores(p.data || []);
      setBancos(b.data || []);
      const availableSites = s.data || [];
      const preferredSiteId = resolvePreferredSiteId(availableSites, {
        currentSiteId: form.sitio_id || filtroSitio,
      });
      setSitios(availableSites);
      setForm((prev) => ({
        ...prev,
        sitio_id: resolvePreferredSiteId(availableSites, {
          currentSiteId: prev.sitio_id,
          fallbackSiteId: preferredSiteId,
        }),
      }));
      setFiltroSitio((prev) =>
        resolvePreferredSiteId(availableSites, {
          currentSiteId: prev,
          fallbackSiteId: preferredSiteId,
        }),
      );
    } catch (err) { console.error(err); }
  }

  async function loadHistorial() {
    setLoading(true);
    const orgId = profile.organization_id;
    try {
      const start = buildLocalDateRangeTimestamp(fechaDesde);
      const end = buildLocalDateRangeTimestamp(fechaHasta, true);

      let query = supabase
        .from('movimientos_financieros')
        .select('*, banks(name), sitios(nombre)')
        .eq('organization_id', orgId)
        .eq('categoria', 'Pago')
        .gte('fecha_operacion', start)
        .lte('fecha_operacion', end)
        .order('created_at', { ascending: false });

      if (!mostrarEliminados) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setHistorial(data || []);
    } catch (err) { 
      reportHandledError(err, { feature: 'tesoreria:pagos-auditoria' });
      toast.error("Error al sincronizar auditoría");
    } finally {
      setLoading(false);
    }
  }

  // NUEVO: Función para anular (Soft Delete con reversión de saldos completísima)
  const anularMovimiento = async (mov) => {
    setProcessingDeleteId(mov.id);
    const toastId = toast.loading('Revertiendo saldos y anulando pago...');

    try {
      let restoredProvBalance = null;
      let linkedPurchaseIds = [];

      // 1. Revertir saldo Banco (Devolver dinero) de forma ATÓMICA. Antes: si la
      //    lectura fallaba se saltaba en silencio (no devolvía la plata) y dos
      //    anulaciones concurrentes se pisaban. Bug T4 (espejo en Pagos).
      if (mov.bank_id) {
        const { error: bankAdjustError } = await supabase.rpc('adjust_bank_balance', {
          p_bank_id: mov.bank_id,
          p_delta: Number(mov.monto),
        });
        if (bankAdjustError) throw bankAdjustError;
      }

      // 2. Revertir saldo Proveedor (Volver a generar deuda)
      if (mov.entidad_id) {
        const { data: provData } = await supabase.from('proveedores').select('saldo').eq('id', mov.entidad_id).single();
        if (provData) {
            restoredProvBalance = Number(provData.saldo) + Number(mov.monto);
            await supabase.from('proveedores').update({ saldo: restoredProvBalance }).eq('id', mov.entidad_id);
        }
      }

      // 3. Anular la Compra/Recibo vinculada (si existe documento_id)
      if (mov.documento_id) {
          const { data: paymentDocument, error: paymentDocumentError } = await supabase
            .from('compras')
            .select('id, items')
            .eq('id', mov.documento_id)
            .maybeSingle();
          if (paymentDocumentError) throw paymentDocumentError;

          linkedPurchaseIds = parseProviderPaymentApplications(paymentDocument?.items).map(
            (application) => application.purchase_id,
          );

          await supabase.from('compras').update({
              estado: 'ANULADO',
              observaciones: `(ANULADO) ${mov.descripcion}`
          }).eq('id', mov.documento_id);
          
          // 3.1 Registrar el impacto en cuenta corriente del proveedor
          if (mov.entidad_id) {
            await supabase.from('proveedor_movimientos').insert([{
              organization_id: profile.organization_id,
              proveedor_id: mov.entidad_id,
              compra_id: mov.documento_id,
              tipo: 'Anulación de Pago',
              monto: Number(mov.monto), // Monto en positivo para reestablecer la deuda
              saldo_resultante: restoredProvBalance,
              fecha: new Date().toISOString(),
            }]);
          }
      }
      if (mov.entidad_id && !mov.documento_id) {
        await supabase.from('proveedor_movimientos').insert([{
          organization_id: profile.organization_id,
          proveedor_id: mov.entidad_id,
          tipo: 'Anulacion de Pago',
          monto: Number(mov.monto),
          saldo_resultante: restoredProvBalance,
          fecha: new Date().toISOString(),
        }]);
      }

      // 4. Soft Delete del Movimiento Financiero (ponemos a 0 para que no sume en reportes)
      const { error } = await supabase.from('movimientos_financieros')
        .update({ 
          is_active: false, 
          monto: 0,
          descripcion: `(ANULADO) ${mov.descripcion}` 
        })
        .eq('id', mov.id);

      if (error) throw error;

      if (mov.entidad_id && linkedPurchaseIds.length > 0) {
        await recomputeProviderPurchaseStatuses({
          organizationId: profile.organization_id,
          providerId: mov.entidad_id,
          purchaseIds: linkedPurchaseIds,
          supabaseClient: supabase,
        });
      }

      toast.success("Pago anulado. Dinero devuelto al banco y deuda restituida.", { id: toastId });
      
      setConfirmDeleteId(null);
      setExpandedId(null);
      await loadMasters(); // Recargar saldos de bancos y proveedores
      await loadHistorial(); // Recargar tabla

    } catch (error) {
      console.error(error);
      toast.error("Error crítico al anular. Revise consola.", { id: toastId });
    } finally {
      setProcessingDeleteId(null);
    }
  };

  const persistPago = async ({ submission, allocations = [], purchases = [] }) => {
    setSaving(true);
    try {
      const orgId = profile.organization_id;
      const monto = Number(submission?.monto || 0);
      const prov = proveedores.find((item) => item.id === submission.proveedor_id);
      const banco = bancos.find((item) => item.id === submission.bank_id);
      const operationTimestamp = buildLocalOperationTimestamp(submission.fecha);

      // Egreso ATÓMICO del banco (un solo UPDATE con lock de fila) en vez del
      // read-modify-write con saldo stale que sufría lost updates. Bug T3/T8 (espejo).
      const { data: nuevoSaldoBanco, error: updateBankError } = await supabase.rpc('adjust_bank_balance', {
        p_bank_id: submission.bank_id,
        p_delta: -monto,
      });
      if (updateBankError) throw updateBankError;

      const nuevoSaldoProv = (Number(prov?.saldo) || 0) - monto;
      const { error: updateProviderError } = await supabase
        .from('proveedores')
        .update({ saldo: nuevoSaldoProv })
        .eq('id', submission.proveedor_id);
      if (updateProviderError) throw updateProviderError;

      let compraId = null;
      const numRef = `PAGO-${Math.floor(Math.random() * 100000)}`;
      const normalizedAllocations = (Array.isArray(allocations) ? allocations : [])
        .map((document) => ({
          purchase_id: document.id,
          amount: Math.max(0, normalizeMoney(document.allocation)),
        }))
        .filter((allocation) => allocation.purchase_id && allocation.amount > 0);
      const paymentItems = buildProviderPaymentItems(normalizedAllocations, purchases);
      const compraPayload = {
        proveedor_id: submission.proveedor_id,
        proveedor_nombre: prov?.razon_social,
        tipo_comprobante: 'Pago',
        numero_comprobante: numRef,
        total_general: monto,
        fecha: operationTimestamp,
        estado: 'Pagada',
        observaciones: `Pago desde Tesoreria (${banco?.name || 'Caja'}). ${submission.obs || ''}`.trim(),
        organization_id: orgId,
        sitio_id: submission.sitio_id,
      };

      if (paymentItems.length > 0) {
        compraPayload.items = paymentItems;
      }

      const { data: compraData, error: compraErr } = await supabase
        .from('compras')
        .insert([compraPayload])
        .select('id')
        .single();
      if (compraErr) throw compraErr;

      if (compraData) {
        compraId = compraData.id;
        const { error: providerMovementError } = await supabase.from('proveedor_movimientos').insert([
          {
            organization_id: orgId,
            proveedor_id: submission.proveedor_id,
            compra_id: compraId,
            tipo: 'Pago',
            monto: -monto,
            saldo_resultante: nuevoSaldoProv,
            fecha: operationTimestamp,
          },
        ]);
        if (providerMovementError) throw providerMovementError;
      }

      const { error: financialMovementError } = await supabase.from('movimientos_financieros').insert([
        {
          organization_id: orgId,
          bank_id: submission.bank_id,
          sitio_id: submission.sitio_id,
          tipo: 'Egreso',
          categoria: 'Pago',
          monto,
          saldo_resultante: nuevoSaldoBanco,
          entidad_tipo: 'Proveedor',
          entidad_id: submission.proveedor_id,
          documento_tipo: 'Pago',
          documento_id: compraId,
          descripcion: `Pago a: ${prov?.razon_social || 'Proveedor'}. ${submission.obs || ''}`.trim(),
          fecha_operacion: operationTimestamp,
          user_id: profile.id,
          is_active: true,
        },
      ]);
      if (financialMovementError) throw financialMovementError;

      if (normalizedAllocations.length > 0) {
        await recomputeProviderPurchaseStatuses({
          organizationId: orgId,
          providerId: submission.proveedor_id,
          purchaseIds: normalizedAllocations.map((allocation) => allocation.purchase_id),
          supabaseClient: supabase,
        });
      }

      closeConciliationModal(true);
      setIsSuccess(true);
      toast.success('PAGO REGISTRADO CORRECTAMENTE');

      setTimeout(async () => {
        resetPagoForm();
        setIsSuccess(false);
        setSaving(false);
        await loadMasters();
        await loadHistorial();
      }, 1500);
    } catch (e) {
      toast.error('Error técnico al procesar el pago');
      console.error(e);
      setSaving(false);
    }
  };

  const guardarPagoLegacy = async (e) => {
    e.preventDefault();
    if (!form.proveedor_id || !form.bank_id || !form.sitio_id || !form.amount) {
      return toast.error("Complete todos los campos obligatorios");
    }
    
    setSaving(true);
    try {
        const orgId = profile.organization_id;
        const monto = parseLocalizedNumber(form.amount);
        if (!monto || monto <= 0) {
          toast.error("Ingresá un monto válido");
          setSaving(false);
          return;
        }
        const prov = proveedores.find(p => p.id === form.proveedor_id);
        const banco = bancos.find(b => b.id === form.bank_id);
        const operationTimestamp = buildLocalOperationTimestamp(form.fecha);

        // 1. Actualizar Saldo Real del Banco (Egreso) de forma ATÓMICA (lock de
        //    fila) y con chequeo de error. Antes: read-modify-write ciego que
        //    sufría lost updates y no detectaba fallos del update. Bug T8.
        const { data: nuevoSaldoBanco, error: bankAdjustError } = await supabase.rpc('adjust_bank_balance', {
            p_bank_id: form.bank_id,
            p_delta: -monto,
        });
        if (bankAdjustError) throw bankAdjustError;

        // 2. Actualizar Cuenta Corriente del Proveedor
        const nuevoSaldoProv = (Number(prov.saldo) || 0) - monto;
        await supabase.from('proveedores').update({ saldo: nuevoSaldoProv }).eq('id', form.proveedor_id);

        // 3. Registro Histórico en Compras (El comprobante de pago que va al proveedor)
        let compraId = null;
        const numRef = `PAGO-${Math.floor(Math.random()*100000)}`;
        
        const { data: compraData, error: compraErr } = await supabase.from('compras').insert([{
            proveedor_id: form.proveedor_id, 
            proveedor_nombre: prov.razon_social, 
            tipo_comprobante: 'Pago',
            numero_comprobante: numRef,
            total_general: monto, 
            fecha: operationTimestamp, 
            estado: 'Pagada',
            observaciones: `Pago desde Tesorería (${banco.name}). ${form.obs}`, 
            organization_id: orgId,
            sitio_id: form.sitio_id
        }]).select('id').single();
        if (compraErr) throw compraErr;

        if (compraData) {
            compraId = compraData.id;
            // 3.1. Impacto exacto en la cuenta corriente (historial de proveedor)
            const { error: providerMovementError } = await supabase.from('proveedor_movimientos').insert([{
                organization_id: orgId,
                proveedor_id: form.proveedor_id,
                compra_id: compraId,
                tipo: 'Pago',
                monto: -monto,
                saldo_resultante: nuevoSaldoProv,
                fecha: operationTimestamp,
            }]);
            if (providerMovementError) throw providerMovementError;
        }

        // 4. Registrar en Tabla Maestra 'movimientos_financieros' (Flujo de fondos)
        const { error: financialMovementError } = await supabase.from('movimientos_financieros').insert([{
            organization_id: orgId,
            bank_id: form.bank_id,
            sitio_id: form.sitio_id,
            tipo: 'Egreso',
            categoria: 'Pago',
            monto: monto,
            saldo_resultante: nuevoSaldoBanco,
            entidad_tipo: 'Proveedor',
            entidad_id: form.proveedor_id,
            documento_tipo: 'Pago',
            documento_id: compraId, // Vinculamos para poder anular luego
            descripcion: `Pago a: ${prov.razon_social}. ${form.obs}`,
            fecha_operacion: operationTimestamp,
            user_id: profile.id,
            is_active: true
        }]);
        if (financialMovementError) throw financialMovementError;

        setIsSuccess(true);
        toast.success("PAGO REGISTRADO CORRECTAMENTE");
        
        setTimeout(async () => {
          setForm({ proveedor_id: '', bank_id: '', sitio_id: '', amount: '', obs: '', fecha: today });
          setBusquedaProveedor('');
          setShowProveedorList(false);
          setIsSuccess(false);
          setSaving(false);
          await loadMasters();
          await loadHistorial();
        }, 1500);

    } catch (e) { 
      toast.error("Error técnico al procesar el pago");
      console.error(e);
      setSaving(false);
    }
  };

  // Mapea una compra abierta al formato que espera AccountReconciliationModal,
  // conservando id / open_amount / allocation que usan los handlers y persistPago.
  const mapPurchaseToConciliationDoc = (purchase) => ({
    ...purchase,
    kindLabel: purchase.tipo_comprobante || 'Compra',
    numberLabel: purchase.numero_comprobante ? `N° ${purchase.numero_comprobante}` : '',
    title: `${purchase.tipo_comprobante || 'Compra'} ${purchase.numero_comprobante || ''}`.trim(),
    dateLabel: formatConciliationDate(purchase.fecha || purchase.created_at),
    paymentConditionLabel: purchase.condicion_pago || '',
    description: purchase.observaciones || '',
  });

  const guardarPago = async (e) => {
    e.preventDefault();
    if (!form.proveedor_id || !form.bank_id || !form.sitio_id || !form.amount) {
      return toast.error('Complete todos los campos obligatorios');
    }

    const monto = parseLocalizedNumber(form.amount);
    if (!monto || monto <= 0) {
      return toast.error('Ingresá un monto válido');
    }

    const submission = {
      proveedor_id: form.proveedor_id,
      bank_id: form.bank_id,
      sitio_id: form.sitio_id,
      fecha: form.fecha,
      obs: form.obs,
      monto,
    };

    setPreparingConciliation(true);
    try {
      // Buscar compras impagas del proveedor para ofrecer conciliación.
      const { data: openPurchases, error: openPurchasesError } = await fetchOpenProviderPurchases({
        organizationId: profile.organization_id,
        providerId: form.proveedor_id,
        supabaseClient: supabase,
      });
      if (openPurchasesError) throw openPurchasesError;

      const purchases = Array.isArray(openPurchases) ? openPurchases : [];

      // El popout SIEMPRE se abre al cargar un pago. Con facturas pendientes,
      // muestra las asignaciones sugeridas (más antiguas primero); sin facturas,
      // muestra el estado vacío y queda disponible "pago a cuenta".
      const totalOpen = purchases.reduce(
        (acc, purchase) => acc + Math.max(0, normalizeMoney(purchase.open_amount)),
        0,
      );
      const suggestedDocs = buildSuggestedAllocations(purchases, monto).map(
        mapPurchaseToConciliationDoc,
      );

      setConciliationPurchases(purchases);
      setConciliationDocs(suggestedDocs);
      setConciliationContext({
        submission,
        // El excedente sobre lo adeudado queda a favor del proveedor: solo
        // exigimos cubrir lo que las facturas pueden absorber.
        requiredAllocation: Math.min(monto, totalOpen),
      });
      setConciliationOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('No se pudieron cargar las facturas pendientes');
    } finally {
      setPreparingConciliation(false);
    }
  };

  // "No aplicar a ninguna factura": registra el pago a cuenta del proveedor,
  // sin asignarlo a comprobantes (queda saldo a favor en la cuenta corriente).
  const confirmarPagoACuenta = async () => {
    if (!conciliationContext?.submission) return;
    await persistPago({
      submission: conciliationContext.submission,
      allocations: [],
      purchases: [],
    });
  };

  const confirmarPagoConciliado = async () => {
    if (!conciliationContext?.submission) return;
    const assignedAmount = sumAllocationAmounts(conciliationDocs);
    if (assignedAmount + 0.000001 < Number(conciliationContext.requiredAllocation || 0)) {
      return toast.error('Todavía falta asignar parte del pago');
    }

    await persistPago({
      submission: conciliationContext.submission,
      allocations: conciliationDocs,
      purchases: conciliationPurchases,
    });
  };

  // Filtrado final en memoria (Sitio y Búsqueda de texto)
  const historialFiltrado = useMemo(() => {
    let filtrado = historial;
    
    if (filtroSitio) {
        filtrado = filtrado.filter(h => h.sitio_id === filtroSitio);
    }
    
    if (q.trim()) {
        const needle = q.trim().toLowerCase();
        filtrado = filtrado.filter(h => {
            return (h.descripcion || '').toLowerCase().includes(needle) ||
                   (h.banks?.name || '').toLowerCase().includes(needle);
        });
    }
    return filtrado;
  }, [historial, filtroSitio, q]);

  // KPI CALCULADOS
  const kpis = useMemo(() => {
    const active = historialFiltrado.filter(h => h.is_active && !String(h.descripcion || '').includes('(ANULADO)'));
    const total = active.reduce((acc, curr) => acc + Number(curr.monto || 0), 0);
    const count = active.length;
    return { total, count };
  }, [historialFiltrado]);

  const seleccionarProveedor = (proveedor) => {
    setForm((prev) => ({ ...prev, proveedor_id: proveedor.id }));
    setBusquedaProveedor('');
    setShowProveedorList(false);
  };

  const resetProveedor = () => {
    setForm((prev) => ({ ...prev, proveedor_id: '' }));
    setBusquedaProveedor('');
    setShowProveedorList(false);
  };

  if (loading && historial.length === 0 && proveedores.length === 0) return (
    <div className="h-screen flex items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin text-[#51A7FD]" size={40} />
    </div>
  );

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER - Estilo Productos */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <ArrowUpCircle size={18} className="text-[#F05A57]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Pagos a Proveedores</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cancelación de pasivos y salida de fondos</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
           <button
             onClick={refrescarTodo}
             disabled={refreshing}
             className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-2xl shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
           >
             <RefreshCcw size={16} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Refrescando...' : 'Refrescar'}
           </button>
        </div>
      </div>

      {/* KPIs - Estilo Productos */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-[#F05A57]/30 transition-all">
          <div className="flex items-center justify-between relative z-10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Egresos</p>
            <DollarSign size={16} className="text-slate-300 group-hover:text-[#F05A57] transition-colors" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-rose-600 tracking-tight relative z-10">
             -${kpis.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En periodo seleccionado</p>
          <div className="absolute -right-4 -bottom-4 text-rose-50 opacity-10 group-hover:opacity-20 transition-opacity">
              <DollarSign size={80} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-slate-300 transition-all">
          <div className="flex items-center justify-between relative z-10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Operaciones</p>
            <Activity size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight relative z-10">{kpis.count}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pagos emitidos</p>
          <div className="absolute -right-4 -bottom-4 text-slate-100 opacity-50 group-hover:opacity-100 transition-opacity">
              <Activity size={80} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-[#51A7FD]/30 transition-all">
            <div className="flex items-center justify-between relative z-10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Periodo Analizado</p>
                <Calendar size={16} className="text-slate-300 group-hover:text-[#51A7FD] transition-colors" />
            </div>
            <p className="mt-1.5 text-xl font-black text-[#51A7FD] tracking-tight truncate relative z-10">
                {fechaDesde === fechaHasta ? new Date(fechaDesde).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : 'Rango Activo'}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Filtro aplicado</p>
             <div className="absolute -right-4 -bottom-4 text-blue-50 opacity-10 group-hover:opacity-20 transition-opacity">
              <Calendar size={80} />
          </div>
        </div>
      </div>

      {/* CUERPO PRINCIPAL (FORMULARIO + TABLA) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* PANEL DE CARGA DIRECTA */}
        <div className="space-y-2">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
             {/* CABECERA NEGRA (Estilo Productos) */}
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowUpCircle size={16} className="text-[#F05A57]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  Nueva Orden de Pago
                </span>
              </div>
            </div>

            <div className="p-5">
              <form onSubmit={guardarPago} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Proveedor / Entidad</label>
                  <div className="relative" ref={proveedorContainerRef}>
                    <div className="relative">
                      <Truck className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                      <input
                        type="text"
                        required={!proveedorSeleccionado}
                        className="w-full bg-slate-50 border border-slate-100 py-3 pl-9 pr-24 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner"
                        placeholder={proveedorSeleccionado ? proveedorSeleccionado.razon_social : 'Buscar proveedor...'}
                        value={proveedorSeleccionado ? '' : busquedaProveedor}
                        onFocus={() => setShowProveedorList(true)}
                        onChange={(e) => {
                          setBusquedaProveedor(e.target.value);
                          setShowProveedorList(true);
                        }}
                      />
                      {proveedorSeleccionado && (
                        <button
                          type="button"
                          onClick={resetProveedor}
                          className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-white border border-slate-200 hover:bg-slate-50"
                        >
                          Cambiar
                        </button>
                      )}
                    </div>

                    {proveedorSeleccionado && (
                      <p className="mt-1 text-[10px] font-bold text-slate-500">
                        Saldo actual: {formatCurrencyAr(proveedorSeleccionado.saldo || 0)}
                      </p>
                    )}

                    {showProveedorList && !proveedorSeleccionado && (
                      <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden max-h-[240px] overflow-y-auto">
                        {proveedoresFiltrados.map((proveedor) => (
                          <button
                            key={proveedor.id}
                            type="button"
                            onClick={() => seleccionarProveedor(proveedor)}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 transition border-b last:border-b-0 border-slate-100"
                          >
                            <div className="text-[11px] font-black uppercase text-slate-800">
                              {proveedor.razon_social}
                            </div>
                            <div className="text-[10px] font-bold text-slate-400">
                              Saldo: {formatCurrencyAr(proveedor.saldo || 0)}
                            </div>
                          </button>
                        ))}
                        {proveedores.length === 0 && (
                          <div className="p-4 text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">
                            Sin proveedores
                          </div>
                        )}
                        {proveedores.length > 0 && proveedoresFiltrados.length === 0 && (
                          <div className="p-4 text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">
                            Sin coincidencias
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Sucursal (Imputación)</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner" 
                    value={form.sitio_id} onChange={e=>setForm({...form, sitio_id:e.target.value})} required
                  >
                    <option value="">-- CENTRO DE COSTOS --</option>
                    {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Cuenta de Origen (Banco/Caja)</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner" 
                    value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})} required
                  >
                    <option value="">-- SELECCIONE CUENTA --</option>
                    {bancos.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} (Disp: {formatCurrencyAr(b.current_balance || 0)})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Importe Exacto</label>
                    <LocalizedNumberInput
                      required
                      decimals={2}
                      selectOnFocus
                      className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[12px] font-black text-rose-600 outline-none focus:border-[#51A7FD] shadow-inner"
                      value={form.amount}
                      onValueChange={(display) => setForm({ ...form, amount: display })}
                      placeholder="0,00"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha</label>
                    <input 
                      type="date" 
                      className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner" 
                      value={form.fecha} onChange={e=>setForm({...form, fecha:e.target.value})} 
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Observaciones</label>
                  <textarea 
                    placeholder="Ref. Transferencia, detalle, etc." 
                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] h-20 shadow-inner resize-none"
                    value={form.obs} onChange={e => setForm({...form, obs: e.target.value})} 
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving || isSuccess || preparingConciliation}
                  className={`w-full py-4 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg mt-2
                    ${isSuccess
                      ? 'bg-[#3ff2aa] text-[#0B2A36]'
                      : 'bg-[#3ff2aa] text-[#0B2A36] hover:brightness-105 active:scale-95 shadow-emerald-50'}`}
                >
                  {isSuccess ? <><Check size={18} strokeWidth={4} className="animate-in zoom-in" /> Procesado</> : (saving || preparingConciliation) ? <><RefreshCcw size={18} className="animate-spin" /> {preparingConciliation ? 'Buscando facturas...' : 'Procesando...'}</> : 'Confirmar Egreso'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* AUDITORÍA FILTRABLE */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* FILTROS (Estilo Productos) */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
            <div className="flex flex-col xl:flex-row gap-3 items-end">
              
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="BUSCAR PROVEEDOR, BANCO..."
                    className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase transition-all"
                />
              </div>

              <div className="flex items-center gap-2 w-full xl:w-auto">
                  <div className="flex-1 space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Sucursal</label>
                    <select value={filtroSitio} onChange={e => setFiltroSitio(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold outline-none cursor-pointer uppercase shadow-inner">
                      <option value="">TODAS LAS SUCURSALES</option>
                      {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Desde</label>
                    <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold uppercase outline-none shadow-inner" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Hasta</label>
                    <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold uppercase outline-none shadow-inner" />
                  </div>
              </div>
              
              <div className="flex items-center gap-2 mb-0.5">
                <button 
                  onClick={() => setMostrarEliminados(!mostrarEliminados)}
                  className={`p-2.5 rounded-xl border transition-all ${mostrarEliminados ? 'bg-rose-50 text-rose-500 border-rose-200' : 'bg-slate-50 text-slate-400 border-slate-100 hover:text-slate-600'}`}
                  title={mostrarEliminados ? "Ocultar anulados" : "Mostrar anulados"}
                >
                  {mostrarEliminados ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
          </div>

          {/* TABLA DE AUDITORÍA (Estilo Productos) */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[450px]">
            <div className="bg-[#0B2A36] px-6 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  Historial de Pagos Emitidos
                </span>
              </div>
            </div>

            <div className="overflow-x-auto w-full">
                <table className="w-full text-left min-w-[600px]">
                <thead>
                    <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="px-6 py-4">Fecha / Hora</th>
                    <th className="px-6 py-4">Cuenta Origen</th>
                    <th className="px-6 py-4">Descripción</th>
                    <th className="px-6 py-4 text-right">Monto Neto</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 font-sans">
                    {historialFiltrado.length === 0 ? (
                        <tr>
                            <td colSpan={4} className="py-20 text-center opacity-40 italic text-[10px] font-black uppercase tracking-widest">
                                Sin operaciones en el periodo
                            </td>
                        </tr>
                    ) : (
                        historialFiltrado.map(h => {
                            const isAnulado = !h.is_active || String(h.descripcion || '').includes('(ANULADO)');
                            return (
                                <React.Fragment key={h.id}>
                                    <tr 
                                        onClick={() => {
                                            if(!isAnulado) {
                                                setExpandedId(expandedId === h.id ? null : h.id);
                                                setConfirmDeleteId(null);
                                            }
                                        }} 
                                        className={`transition-colors group ${isAnulado ? 'bg-rose-50/30 opacity-70' : 'hover:bg-blue-50/20 cursor-pointer'}`}
                                    >
                                        <td className="px-6 py-4 text-[10px] font-bold text-slate-500">
                                            <div className="flex items-center gap-2">
                                                <Calendar size={12} className={isAnulado ? 'text-slate-300' : 'text-slate-400'} />
                                                {new Date(h.fecha_operacion).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </td>
                                        <td className={`px-6 py-4 text-[10px] font-black uppercase tracking-tight ${isAnulado ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                            {h.banks?.name}
                                        </td>
                                        <td className={`px-6 py-4 text-[10px] font-bold uppercase truncate max-w-[200px] ${isAnulado ? 'text-slate-400' : 'text-slate-700'}`}>
                                            {h.descripcion || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <span className={`font-black text-[12px] ${isAnulado ? 'text-slate-400 line-through' : 'text-rose-600'}`}>
                                            -${Number(h.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                            </span>
                                        </td>
                                    </tr>
                                    
                                    {/* DETALLE EXPANDIBLE PARA ANULACIÓN INLINE (Estilo idéntico a Caja) */}
                                    {expandedId === h.id && !isAnulado && (
                                        <tr className="bg-slate-50 animate-in fade-in slide-in-from-top-1">
                                            <td colSpan="4" className="px-6 py-4 border-b border-slate-100">
                                                <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white border border-rose-100 p-4 rounded-xl shadow-inner">
                                                    <div className="flex items-center gap-3">
                                                        <AlertCircle size={18} className="text-rose-500"/>
                                                        <div>
                                                            <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Opciones de Registro</p>
                                                            <p className="text-[10px] font-bold text-slate-500 mt-0.5">Si anulas este registro, se revertirán los saldos en Bancos y el Proveedor volverá a tener esta deuda.</p>
                                                        </div>
                                                    </div>
                                                    
                                                    {confirmDeleteId === h.id ? (
                                                        <div className="flex items-center gap-2 animate-in zoom-in bg-rose-50 p-1.5 rounded-xl border border-rose-200">
                                                            <span className="text-[10px] font-black text-rose-600 uppercase px-2">¿Confirmar?</span>
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); anularMovimiento(h); }}
                                                                disabled={processingDeleteId === h.id}
                                                                className="flex items-center gap-1 px-3 py-1.5 bg-rose-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-rose-600 transition-colors shadow-sm"
                                                            >
                                                                {processingDeleteId === h.id ? <RefreshCcw size={12} className="animate-spin" /> : <Check size={12} />} Sí, borrar
                                                            </button>
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                                                className="px-3 py-1.5 bg-white border border-slate-200 text-slate-500 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-slate-50 transition-colors shadow-sm"
                                                            >
                                                                No
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setConfirmDeleteId(h.id);
                                                            }}
                                                            className="flex items-center gap-2 px-4 py-2 bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm whitespace-nowrap"
                                                        >
                                                            <Trash2 size={14} /> Anular Registro
                                                        </button>
                                                    )}
                                                </div>
                                                
                                                {/* Meta data extra del movimiento */}
                                                <div className="mt-3 flex gap-6 px-2 opacity-60">
                                                    <span className="text-[9px] font-black text-slate-500 uppercase">Sucursal: <span className="text-slate-700">{h.sitios?.nombre || 'Administración Central'}</span></span>
                                                    <span className="text-[9px] font-black text-slate-500 uppercase">Saldo Resultante (Momento de op): <span className="text-slate-700">${Number(h.saldo_resultante || 0).toLocaleString()}</span></span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            )
                        })
                    )}
                </tbody>
                </table>
            </div>
          </div>
        </div>
      </div>

      <AccountReconciliationModal
        open={conciliationOpen}
        title="Conciliar pago con facturas"
        subtitle={proveedorSeleccionado?.razon_social || ''}
        documents={conciliationDocs}
        totalAmount={Number(conciliationContext?.submission?.monto || 0)}
        requiredAllocation={Number(conciliationContext?.requiredAllocation || 0)}
        onAllocationChange={handleConciliationAllocationChange}
        onClose={() => closeConciliationModal()}
        onConfirm={confirmarPagoConciliado}
        onSkip={confirmarPagoACuenta}
        skipLabel="No aplicar a ninguna factura"
        confirmLabel="Confirmar pago"
        loading={saving}
        helperText="Asigná el pago a una o más facturas pendientes, o registralo a cuenta con “No aplicar a ninguna factura”. El excedente queda a favor del proveedor."
        emptyMessage="Este proveedor no tiene facturas pendientes. Podés registrarlo como pago a cuenta."
      />

    </div>
  );
}

export default TesoreriaPagos;
